/**
 * Detail drawer for one Submission Pack item (Task B5).
 *
 * The SHELL is a deliberate copy of `RequirementDrawer`'s, not a shared
 * abstraction (design decision D-11): that component is hard-typed to
 * `ExtractedComplianceItem`, and generalising it would drag position /
 * criticality / coverage into a surface that has none of them. What is copied
 * is the shape — `useIsDesktop` at 1024px, a plain `<aside role="complementary">`
 * beside the list on desktop (NOT a Radix Dialog, so the list stays clickable),
 * a `Sheet side="right" hideClose` below lg, fixed head + scrolling body.
 *
 * No thread / discussion UI (design decision D-10) — requirement threads are a
 * later PR, and the pack drawer ships facts, link, owner and acknowledgement.
 * "link OR acknowledgement", strictly: an acknowledge-class item has nothing to
 * link, so it gets the tick in that slot, exactly as its row does.
 */

import { useEffect, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { CitationLink } from '@/components/extraction/CitationLink'
import { OwnerPicker } from '@/components/extraction/OwnerPicker'
import { PackReadinessPill } from '@/components/pack/PackReadinessPill'
import { useIsDesktop } from '@/hooks/useIsDesktop'
import { usePreviewStore } from '@/stores/preview-store'
import type { PackItem, TriageClass } from '@/lib/api'

// ===================
// Constants
// ===================

/**
 * Fields and overlays that own their own keys — the window-level Escape handler
 * must not steal Escape from them. Copied from `KEY_CAPTURE_SELECTOR` in
 * `src/components/extraction/ExtractionTable.tsx` (not exported there, and
 * importing it would pull that whole table module into this drawer). Without
 * this, Escape dismissing the status dropdown, the owner picker or the add
 * dialog would close the drawer underneath them in the same keystroke.
 */
const KEY_CAPTURE_SELECTOR =
  'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"], [role="listbox"], [role="combobox"]'

/** Singular, because this names ONE item — the list headings are the plurals. */
const TRIAGE_LABELS: Record<TriageClass, string> = {
  response: 'Response',
  form: 'Form',
  attachment: 'Attachment',
  acknowledge: 'Acknowledgement',
}

// ===================
// Types
// ===================

interface TeamMember {
  id: string
  userId: string
  fullName: string | null
  avatarUrl: string | null
}

export interface SubmissionItemDrawerProps {
  /** `null` renders nothing — the host keeps the drawer mounted and passes the active row. */
  item: PackItem | null
  onClose: () => void
  readOnly: boolean
  /** The pack deadline, for the readiness pill's "Missing" countdown. */
  deadline?: string | null
  teamMembers: TeamMember[]
  onOwnerChange: (itemId: string, userId: string | null) => void
  onAcknowledgeChange: (itemId: string, acknowledge: boolean) => void
  /** The page's `PackLinkCell` render, passed straight through (Task B4). */
  linkSlot?: (item: PackItem) => ReactNode
}

// ===================
// Body
// ===================

interface Fact {
  label: string
  value: string
}

/** Reference facts the tender stated. Absent ones are omitted, not blanked. */
function referenceFacts(item: PackItem): Fact[] {
  const facts: Fact[] = []
  if (item.format) facts.push({ label: 'Format', value: item.format })
  if (item.copies) facts.push({ label: 'Copies', value: item.copies })
  if (item.method) facts.push({ label: 'Method', value: item.method })
  return facts
}

/**
 * The one-line gloss under the readiness pill.
 *
 * The RAW limit string is always shown when the tender gave one — a page limit
 * or an unparsed phrase still has to reach the reader. The word count only
 * joins it when the server actually parsed a word limit AND counted words.
 */
function wordCountDetail(item: PackItem): string | null {
  const { wordLimit, totalWords } = item.derived
  if (wordLimit == null || totalWords == null) return null
  return `${totalWords.toLocaleString()} of ${wordLimit.toLocaleString()} words drafted`
}

/** AU English, matching `KeyDatesExtraction`'s formatter. */
function formatAcknowledgedAt(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

interface DrawerBodyProps {
  item: PackItem
  onClose: () => void
  readOnly: boolean
  deadline: string | null
  teamMembers: TeamMember[]
  onOwnerChange: SubmissionItemDrawerProps['onOwnerChange']
  onAcknowledgeChange: SubmissionItemDrawerProps['onAcknowledgeChange']
  linkSlot: SubmissionItemDrawerProps['linkSlot']
  /** The sheet needs a Radix <SheetTitle> for its dialog label; the aside uses a plain <h2>. */
  titleAs?: 'h2' | 'sheet'
}

function DrawerBody({
  item,
  onClose,
  readOnly,
  deadline,
  teamMembers,
  onOwnerChange,
  onAcknowledgeChange,
  linkSlot,
  titleAs = 'h2',
}: DrawerBodyProps) {
  const titleClassName = 'text-sm font-medium'
  const title =
    titleAs === 'sheet' ? (
      <SheetTitle className={titleClassName}>{item.title}</SheetTitle>
    ) : (
      <h2 className={titleClassName}>{item.title}</h2>
    )

  const facts = referenceFacts(item)
  const detail = wordCountDetail(item)
  // Mirrors the row (`PackList`): an acknowledgement has nothing to link, so the
  // tick takes the link slot's place rather than sitting beside a dead chip.
  const isAcknowledge = item.triageClass === 'acknowledge'

  return (
    <>
      {/* Head — fixed */}
      <div className="flex items-start justify-between gap-2 border-b border-border p-4">
        <div className="min-w-0 space-y-1">
          {title}
          <p className="text-meta">{TRIAGE_LABELS[item.triageClass ?? 'attachment']}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Close details"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body — scrolls */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {item.description && <p className="text-sm text-muted-foreground">{item.description}</p>}

        <div className="space-y-1.5">
          <PackReadinessPill
            readiness={item.readiness}
            triageClass={item.triageClass}
            hasLink={item.link !== null}
            statusSource={item.statusSource}
            deadline={deadline}
            showSource
          />
          {item.limit && <p className="text-meta">{item.limit}</p>}
          {detail && (
            <p className={item.derived.overWordLimit ? 'text-xs text-destructive' : 'text-meta'}>
              {detail}
              {item.derived.overWordLimit ? ' — over the limit' : ''}
            </p>
          )}
        </div>

        {facts.length > 0 && (
          <dl className="space-y-1.5 border-t border-border pt-3">
            {facts.map((fact) => (
              <div key={fact.label} className="flex items-start gap-3">
                <dt className="w-28 shrink-0 text-meta">{fact.label}</dt>
                <dd className="min-w-0 flex-1 text-sm text-foreground">{fact.value}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="space-y-2.5 border-t border-border pt-3">
          {isAcknowledge ? (
            <div className="flex items-start gap-3">
              <span className="w-28 shrink-0 pt-1 text-meta">Acknowledgement</span>
              <div className="min-w-0 flex-1">
                {readOnly ? (
                  <p className="text-sm text-foreground">
                    {item.acknowledgedAt
                      ? `Acknowledged ${formatAcknowledgedAt(item.acknowledgedAt)}`
                      : 'Not acknowledged'}
                  </p>
                ) : (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={item.acknowledgedAt !== null}
                      onCheckedChange={(checked) =>
                        onAcknowledgeChange(item.id, checked === true)
                      }
                      aria-label={`Acknowledge ${item.title}`}
                    />
                    <span className="text-meta">
                      {item.acknowledgedAt
                        ? `Acknowledged ${formatAcknowledgedAt(item.acknowledgedAt)}`
                        : 'Acknowledged'}
                    </span>
                  </label>
                )}
              </div>
            </div>
          ) : (
            linkSlot && (
              <div className="flex items-start gap-3">
                <span className="w-28 shrink-0 pt-1 text-meta">Linked work</span>
                <div className="min-w-0 flex-1">{linkSlot(item)}</div>
              </div>
            )
          )}
          <div className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-meta">Owner</span>
            <OwnerPicker
              value={item.ownerId}
              teamMembers={teamMembers}
              disabled={readOnly}
              onChange={(userId) => onOwnerChange(item.id, userId)}
            />
          </div>
        </div>

        {item.note && (
          <div className="space-y-1.5 border-t border-border pt-3">
            <span className="text-xs font-medium text-foreground">Note</span>
            <p className="text-sm text-muted-foreground">{item.note}</p>
          </div>
        )}

        {item.sourceQuote && (
          <div className="space-y-1.5 border-t border-border pt-3">
            <span className="text-xs font-medium text-foreground">Source</span>
            <blockquote className="border-l-2 border-border pl-2.5 text-xs text-muted-foreground">
              &ldquo;{item.sourceQuote}&rdquo;
            </blockquote>
            <CitationLink
              page={item.sourcePageNumber}
              documentId={item.sourceDocumentId}
              sourceQuote={item.sourceQuote}
              context="pack"
            />
          </div>
        )}
      </div>
    </>
  )
}

// ===================
// Component
// ===================

export function SubmissionItemDrawer({
  item,
  onClose,
  readOnly,
  deadline = null,
  teamMembers,
  onOwnerChange,
  onAcknowledgeChange,
  linkSlot,
}: SubmissionItemDrawerProps) {
  const isDesktop = useIsDesktop()
  const isOpen = item !== null

  /**
   * Escape closes the docked aside. Window-level and armed only while an item
   * is open, so a page with nothing open never touches the page's keys. The
   * Sheet path is skipped — Radix already closes it on Escape, and a second
   * handler would fight it. A document preview opened from the link chip or the
   * citation claims Escape first, exactly as `ExtractionTable` does.
   */
  useEffect(() => {
    if (!isOpen || !isDesktop) return
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // `instanceof Element`, not a cast: a keydown dispatched at `window` has
      // a target with no `closest`, and a cast would crash on it.
      const target = e.target instanceof Element ? e.target : null
      if (target?.closest(KEY_CAPTURE_SELECTOR)) return
      if (usePreviewStore.getState().isOpen) return
      e.preventDefault()
      onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, isDesktop, onClose])

  // Tolerated, not guarded against: the host may hold an id that has left the
  // pack (a removed row, a re-read). Rendering nothing beats crashing.
  if (!item) return null

  const shared = {
    item,
    onClose,
    readOnly,
    deadline,
    teamMembers,
    onOwnerChange,
    onAcknowledgeChange,
    linkSlot,
  }

  if (!isDesktop) {
    return (
      <Sheet open onOpenChange={(open) => !open && onClose()}>
        {/* The body has no single descriptive paragraph to point at — it is a
            fact list — so the description is opted out of explicitly rather
            than left for Radix to warn about. */}
        <SheetContent
          side="right"
          hideClose
          aria-describedby={undefined}
          className="flex w-full flex-col p-0 sm:max-w-md"
        >
          <DrawerBody {...shared} titleAs="sheet" />
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <aside
      role="complementary"
      aria-label="Pack item details"
      className="flex max-h-full w-[400px] min-w-0 max-w-full shrink flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm"
    >
      <DrawerBody {...shared} />
    </aside>
  )
}
